import { useEffect, useRef, useState } from 'react';

// 🧠 Helper: classify by connection type
function classifyByEffectiveType(effectiveType) {
  if (!effectiveType) return 'unknown';
  const et = String(effectiveType).toLowerCase();

  if (['2g', 'slow-2g', 'slow-3g'].includes(et)) return 'moderate';
  if (['3g', '4g', '5g', 'wifi'].includes(et)) return 'good';

  return 'unknown';
}

// 🧠 Helper: classify latency (interval mode)
function classifyByLatency(latencyMs) {
  if (!isFinite(latencyMs)) return 'offline';

  if (latencyMs < 500) return 'good';
  if (latencyMs < 1500) return 'moderate';
  return 'offline';
}

// 🧠 Helper: choose worse between two statuses
function worstStatus(a, b) {
  const rank = { offline: 0, moderate: 1, good: 2, unknown: 3, undefined: 3 };
  return rank[a] <= rank[b] ? a : b;
}

// 🔁 Network Quality Hook
export function useNetworkQuality({
  intervalMs = 15000,
  onLog,
  probeTimeoutMs = 2000,
} = {}) {
  const [state, setState] = useState({
    status: 'unknown',
    effectiveType: undefined,
    downlink: undefined,
    latencyMs: undefined,
    justCameOnline: false,
  });

  const timerRef = useRef(null);
  const backOnlineTimerRef = useRef(null);

  // ✅ OFFLINE DOMINANCE LATCH
  const offlineLatchedRef = useRef(false);
  
  // Track if we've already shown "back online" after recovery
  const recoveryShownRef = useRef(false);

  // --- Fetch probe
  async function pingFetch(url, timeoutMs = probeTimeoutMs) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const start = performance.now();
    try {
      await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        mode: url.startsWith('http') ? 'no-cors' : 'same-origin',
        credentials: url.startsWith('http') ? 'omit' : 'same-origin',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      return performance.now() - start;
    } catch {
      clearTimeout(timeout);
      throw new Error('ping failed');
    }
  }

  // --- Image-based probe
  function pingImage(url, timeoutMs = probeTimeoutMs) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const start = performance.now();
      let done = false;

      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        reject(new Error('timeout'));
      }, timeoutMs);

      img.onload = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(performance.now() - start);
      };

      img.onerror = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(new Error('error'));
      };

      img.src = `${url}?_t=${Date.now()}`;
    });
  }

  // --- Multi-probe
  async function multiProbe() {
    const ts = Date.now();
    const local = [
      `/favicon.ico?_t=${ts}`,
      `/manifest.json?_t=${ts}`,
    ];

    const remote = ['https://www.gstatic.com/generate_204'];

    const results = await Promise.allSettled([
      ...local.map((u) => pingFetch(u, probeTimeoutMs)),
      pingFetch(remote[0], probeTimeoutMs),
    ]);

    // ✅ CRITICAL: Remote probe MUST succeed to confirm true internet connectivity
    const remoteResult = results[results.length - 1]; // Last result is always remote
    const remoteSuccess = remoteResult.status === 'fulfilled';

    if (!remoteSuccess) {
      return { online: false, latencyMs: Infinity };
    }

    const latencies = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => r.value);

    return {
      online: true,
      latencyMs: Math.min(...latencies),
    };
  }

  // --- Measure once (FIXED)
  async function measureOnce(isInterval = false) {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    const online =
      typeof navigator.onLine === 'boolean' ? navigator.onLine : true;

    const effectiveType = conn?.effectiveType;
    const downlink = conn?.downlink;

    let probeOnline = true;
    let latencyMs = Infinity;

    try {
      const probe = await multiProbe();
      probeOnline = probe.online;
      latencyMs = probe.latencyMs;
    } catch {
      probeOnline = false;
      latencyMs = Infinity;
    }

    const latencyStatus = classifyByLatency(latencyMs);
    const connStatus = classifyByEffectiveType(effectiveType);

    let status;
    let justCameOnline = false;

    // 🔒 STEP 1: OFFLINE DOMINATES
    if (!online || !probeOnline || latencyStatus === 'offline') {
      status = 'offline';
      offlineLatchedRef.current = true;
      recoveryShownRef.current = false; // Reset when going offline
      // Clear the back online timer if we go offline again
      if (backOnlineTimerRef.current) {
        clearTimeout(backOnlineTimerRef.current);
        backOnlineTimerRef.current = null;
      }
    }
    // 🔁 STEP 2: ONLY RECOVER ON CONFIRMED GOOD
    else if (offlineLatchedRef.current) {
      if (latencyStatus === 'good' && connStatus === 'good') {
        status = 'good';
        offlineLatchedRef.current = false;
        
        // Only show "back online" if we haven't already shown it for this recovery
        if (!recoveryShownRef.current) {
          justCameOnline = true;
          recoveryShownRef.current = true;

          // Clear any existing timer
          if (backOnlineTimerRef.current) {
            clearTimeout(backOnlineTimerRef.current);
          }

          // Set timer to reset justCameOnline after 5 seconds
          backOnlineTimerRef.current = setTimeout(() => {
            setState((prev) => ({ ...prev, justCameOnline: false }));
            backOnlineTimerRef.current = null;
          }, 5000);
        }
      } else {
        status = 'offline';
      }
    }
    // 🟢 STEP 3: NORMAL FLOW
    else {
      status = worstStatus(connStatus, latencyStatus);
    }

    const snapshot = {
      status,
      effectiveType,
      downlink,
      latencyMs,
      justCameOnline,
    };

    setState(snapshot);
    if (typeof onLog === 'function') onLog(snapshot);
  }

  // --- Lifecycle
  useEffect(() => {
    measureOnce(true);

    function onOnline() {
      // Don't immediately release the latch - let measureOnce verify actual connectivity
      measureOnce(true);
    }

    function onOffline() {
      // Immediately set offline when browser detects offline
      offlineLatchedRef.current = true;
      // Clear the back online timer
      if (backOnlineTimerRef.current) {
        clearTimeout(backOnlineTimerRef.current);
        backOnlineTimerRef.current = null;
      }
      const snapshot = {
        status: 'offline',
        effectiveType: undefined,
        downlink: undefined,
        latencyMs: Infinity,
        justCameOnline: false,
      };
      setState(snapshot);
      if (typeof onLog === 'function') onLog(snapshot);
    }

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', () => measureOnce(true));
    }

    timerRef.current = setInterval(
      () => measureOnce(true),
      intervalMs
    );

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (conn && typeof conn.removeEventListener === 'function') {
        conn.removeEventListener('change', measureOnce);
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (backOnlineTimerRef.current) clearTimeout(backOnlineTimerRef.current);
    };
  }, [intervalMs, probeTimeoutMs]);

  return state;
}

// ⚡ One-time connectivity check (UNCHANGED)
export async function checkConnectivity({ timeoutMs = 1500 } = {}) {
  const conn =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  const effectiveType = conn?.effectiveType;
  const downlink = conn?.downlink;

  async function ping() {
    try {
      const start = performance.now();
      await fetch('https://www.gstatic.com/generate_204', {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
      });
      return performance.now() - start;
    } catch {
      return Infinity;
    }
  }

  const latencyMs = await ping();
  const latencyStatus = classifyByLatency(latencyMs);
  const connStatus = classifyByEffectiveType(effectiveType);
  const final = worstStatus(connStatus, latencyStatus);

  return {
    online: final !== 'offline',
    latencyMs,
    status: final,
  };
}
